import { useEffect } from 'react';
import { tinykeys } from 'tinykeys';

type KeyBindingMap = {
  [keybinding: string]: (event: KeyboardEvent) => void;
};

interface KeyboardShortcutsOptions {
  /**
   * Keybindings configuration object
   */
  keybindings: KeyBindingMap;

  /**
   * Whether the shortcuts are currently enabled
   * @default true
   */
  enabled?: boolean;

  /**
   * Event type to listen for
   * @default 'keydown'
   */
  event?: 'keydown' | 'keyup';

  /**
   * Timeout for sequence keybindings in milliseconds
   * @default 1000
   */
  timeout?: number;

  /**
   * Whether to ignore shortcuts when user is typing in input fields
   * @default true
   */
  ignoreInputFocus?: boolean;
}

/**
 * Check if the event target is an input element or contenteditable
 */
function isInputElement(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) {
    return false;
  }

  const element = target as HTMLElement;

  // Check for input, textarea, select elements
  if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT') {
    return true;
  }

  // Check for contenteditable elements
  if (element.isContentEditable) {
    return true;
  }

  // Check for ARIA input roles
  const role = element.getAttribute('role');
  const inputRoles = [
    'textbox',
    'searchbox',
    'spinbutton',
    'combobox',
    'listbox',
    'slider',
    'radio',
    'checkbox',
    'switch',
    'option',
    'menuitem',
    'menuitemcheckbox',
    'menuitemradio',
  ];

  if (role && inputRoles.includes(role)) {
    return true;
  }

  // Check for buttons with listbox/menu popup indicators (custom selects)
  if (element.getAttribute('aria-haspopup') === 'listbox' || element.getAttribute('aria-haspopup') === 'menu') {
    return true;
  }

  // Check if a listbox/menu is expanded (indicating active selection)
  if (element.getAttribute('aria-expanded') === 'true') {
    return true;
  }

  // Check for multi-line text inputs
  if (element.getAttribute('aria-multiline') === 'true') {
    return true;
  }

  // Check if element is inside a rich text editor, ARIA input, or other input-like container
  const closestInput = element.closest(
    '[contenteditable="true"], [role="textbox"], [role="searchbox"], [role="spinbutton"], [role="combobox"], [role="listbox"], [role="option"], [aria-haspopup="listbox"], [aria-haspopup="menu"], [aria-expanded="true"], .ProseMirror, .editor-container, .rich-text-editor',
  );

  if (closestInput) {
    return true;
  }

  return false;
}

/**
 * Check if a keybinding should be ignored based on the event target
 */
function shouldIgnoreShortcut(event: KeyboardEvent, key: string, ignoreInputFocus: boolean): boolean {
  if (!ignoreInputFocus) {
    return false;
  }

  // Always allow modifier-based shortcuts (Cmd/Ctrl + key)
  if (key.includes('$mod') || key.includes('Meta') || key.includes('Control')) {
    return false;
  }

  // Allow function keys, arrow keys, escape, etc.
  const allowedKeys = [
    'Escape',
    'Enter',
    'Tab',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'F1',
    'F2',
    'F3',
    'F4',
    'F5',
    'F6',
    'F7',
    'F8',
    'F9',
    'F10',
    'F11',
    'F12',
  ];

  if (allowedKeys.includes(event.key)) {
    return false;
  }

  // Ignore single character shortcuts when typing in input fields
  return isInputElement(event.target);
}

/**
 * Create wrapped keybindings that respect input focus
 */
function createWrappedKeybindings(keybindings: KeyBindingMap, ignoreInputFocus: boolean): KeyBindingMap {
  const wrappedKeybindings: KeyBindingMap = {};

  Object.entries(keybindings).forEach(([key, handler]) => {
    wrappedKeybindings[key] = (event: KeyboardEvent) => {
      if (shouldIgnoreShortcut(event, key, ignoreInputFocus)) {
        return;
      }
      (handler as (event: KeyboardEvent) => void)(event);
    };
  });

  return wrappedKeybindings;
}

/**
 * Custom hook for managing keyboard shortcuts with tinykeys
 * Prevents single character shortcuts from triggering when typing in input fields
 * @param options Keyboard shortcuts configuration
 */
function useKeyboardShortcuts(options: KeyboardShortcutsOptions): void {
  const { keybindings, enabled = true, event = 'keydown', timeout = 1000, ignoreInputFocus = true } = options;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const wrappedKeybindings = createWrappedKeybindings(keybindings, ignoreInputFocus);

    const unsubscribe = tinykeys(window, wrappedKeybindings, {
      event,
      timeout,
    });

    return () => {
      unsubscribe();
    };
  }, [keybindings, enabled, event, timeout, ignoreInputFocus]);
}

export default useKeyboardShortcuts;
